import { Temporal } from '@js-temporal/polyfill';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { placeLabel } from '../api/crew.ts';
import {
  answerOf,
  answerOffer,
  closeOffer,
  reoffer,
  withdrawFromOffer,
  type Offer,
} from '../api/offers.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { Popover } from '../components/popover.tsx';
import { AddPeople, OfferForm } from './offer-row.tsx';
import { dayName, zoneName } from './roster-bars.tsx';

// An offer of places opened on the roster, as the operator's OfferPopover board draws it
// (2026-10-26, docs/designs/roster-page.md §2; CB-REQ CRW-04 at v1.994 – v1.995): its places as a
// row of slots, first to accept wins; who it went to, when, and each answer, with Withdraw and
// Offer again; Add people and Record an answer; its fields edited in place, with the note that an
// edit reaches the shifts already taken from it; Save changes and Close the offer in the foot.

const ANSWER_TAG = {
  Taken: 'tag tag-accepted',
  Waiting: 'tag tag-offered',
  Declined: 'tag',
  Withdrawn: 'tag',
} as const;

const hhmm = (local: string) => local.slice(11, 16);

export function OfferPopover({
  offer,
  mayManage,
  seesPrices,
  onChanged,
  onClose,
}: {
  offer: Offer;
  mayManage: boolean;
  seesPrices: boolean;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const o = offer;
  const formId = useId();
  const [adding, setAdding] = useState(false);
  const [recording, setRecording] = useState(false);
  const [declining, setDeclining] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [closing, setClosing] = useState(false);
  const [notAdded, setNotAdded] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setDeclining(null);
      setReason('');
      await onChanged();
    },
  });
  const live = mayManage && o.open;
  const job = o.bookedForJob;
  const sent = (at: string) => {
    const z = Temporal.Instant.from(at).toZonedDateTimeISO(o.window.zone);
    return `${dayName(z.toPlainDate().toString())} ${z.toPlainTime().toString({ smallestUnit: 'minute' })}`;
  };
  const subtitle = [
    `${dayName(o.window.localStart.slice(0, 10))} · ${hhmm(o.window.localStart)} – ${hhmm(o.window.localEnd)} ${zoneName(o.window.zone)}`,
    o.localCallAt !== o.window.localStart ? `call ${hhmm(o.localCallAt)}` : '',
    job ? `J-${job.number} — ${job.name}` : '',
    o.venue || o.location ? placeLabel(o) : 'Held days',
  ]
    .filter(Boolean)
    .join(' · ');
  const state = o.open ? 'Open' : o.closedAt ? 'Closed' : 'Full';

  return (
    <Popover
      title={`Offer · ${o.places} ${o.role ? o.role.name.toLowerCase() + (o.places === 1 ? '' : 's') : o.places === 1 ? 'place' : 'places'}`}
      tag={<span className={`tag ${o.open ? 'tag-offered' : ''}`}>{state}</span>}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        mayManage ? (
          <>
            <Button type="submit" form={formId}>
              Save changes
            </Button>
            <span className="flex-1" />
            {live ? (
              <Button variant="quiet" onClick={() => setClosing(true)}>
                Close the offer…
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      <div className="flex flex-wrap items-center gap-3.5">
        <span className="flex gap-1" role="img" aria-label={`${o.places} places, ${o.taken} taken`}>
          {Array.from({ length: o.places }, (_, i) => (
            <span key={i} className="offer-place" data-taken={i < o.taken ? 'true' : undefined} />
          ))}
        </span>
        <span className="text-[13px]">
          <strong>
            {o.taken} of {o.places} {o.places === 1 ? 'place' : 'places'} taken
          </strong>{' '}
          · first to accept gets a place · sent to {o.recipients.length}
        </span>
      </div>

      {closing ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-tile p-3">
          <p className="text-sm">
            Close it? Nobody else can take a place; the {o.taken} taken stand.
          </p>
          <Button
            disabled={act.isPending}
            onClick={() => act.mutate(() => closeOffer(o.id).then(() => setClosing(false)))}
          >
            Yes, close it
          </Button>
          <Button variant="quiet" onClick={() => setClosing(false)}>
            Back
          </Button>
        </div>
      ) : null}

      <table className="data-table text-sm">
        <thead>
          <tr>
            <th scope="col">Sent to</th>
            <th scope="col">Sent</th>
            <th scope="col">Answer</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {o.recipients.map((r) => {
            const answer = answerOf(r);
            const who = r.crewMember.id;
            const name = r.crewMember.name;
            return (
              <tr key={who}>
                <td>
                  <strong>{name}</strong>
                </td>
                <td>{sent(r.offeredAt)}</td>
                <td>
                  <span className={ANSWER_TAG[answer]}>{answer}</span>
                  {answer === 'Declined' && r.declineReason ? (
                    <span className="ml-1.5 text-xs text-muted">“{r.declineReason}”</span>
                  ) : null}
                  {declining === who ? (
                    <span className="mt-2 flex flex-wrap items-end gap-2">
                      <Field
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                      />
                      <Button
                        className="small-btn"
                        disabled={act.isPending}
                        onClick={() =>
                          act.mutate(() =>
                            answerOffer(o.id, who, {
                              answer: 'declined',
                              reason: reason.trim() || null,
                            }),
                          )
                        }
                      >
                        Decline
                      </Button>
                      <Button
                        variant="quiet"
                        className="small-btn"
                        onClick={() => setDeclining(null)}
                      >
                        Back
                      </Button>
                    </span>
                  ) : null}
                </td>
                <td className="text-right whitespace-nowrap">
                  {live && recording && answer === 'Waiting' && declining !== who ? (
                    <>
                      <Button
                        variant="quiet"
                        className="small-btn"
                        disabled={act.isPending}
                        aria-label={`${name} accepted`}
                        onClick={() =>
                          act.mutate(() => answerOffer(o.id, who, { answer: 'accepted' }))
                        }
                      >
                        Accepted
                      </Button>{' '}
                      <Button
                        variant="quiet"
                        className="small-btn"
                        aria-label={`${name} declined…`}
                        onClick={() => setDeclining(who)}
                      >
                        Declined…
                      </Button>
                    </>
                  ) : null}
                  {mayManage && !recording && answer === 'Waiting' ? (
                    <Button
                      variant="quiet"
                      className="small-btn"
                      disabled={act.isPending}
                      aria-label={`Withdraw ${name}’s offer`}
                      onClick={() => act.mutate(() => withdrawFromOffer(o.id, who))}
                    >
                      Withdraw
                    </Button>
                  ) : null}
                  {live && (answer === 'Declined' || answer === 'Withdrawn') ? (
                    <Button
                      variant="quiet"
                      className="small-btn"
                      disabled={act.isPending}
                      aria-label={`Offer it to ${name} again`}
                      onClick={() => act.mutate(() => reoffer(o.id, who))}
                    >
                      Offer again
                    </Button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {live ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="quiet" className="small-btn" onClick={() => setAdding(!adding)}>
            Add people
          </Button>
          <Button
            variant="quiet"
            className="small-btn"
            aria-pressed={recording}
            onClick={() => setRecording(!recording)}
          >
            Record an answer…
          </Button>
        </div>
      ) : null}
      {adding ? (
        <AddPeople
          offer={o}
          onDone={async (refused) => {
            setNotAdded(refused);
            setAdding(false);
            await onChanged();
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {notAdded.length > 0 ? (
        <div role="alert" className="alert text-sm">
          {notAdded.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ) : null}
      {act.error ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}

      {mayManage ? (
        <>
          <OfferForm
            offer={o}
            seesPrices={seesPrices}
            bare
            formId={formId}
            onDone={() => setSaved(true)}
            onSaved={onChanged}
          />
          {saved ? (
            <p role="status" className="text-sm text-muted">
              Saved.
            </p>
          ) : null}
          <div className="callout">
            <span className="tag tag-accent shrink-0">Editing</span>
            <span>
              A change here reaches every shift already taken from this offer, unless that shift was
              changed by hand. Each person is told as their own change rules say.
            </span>
          </div>
        </>
      ) : null}
    </Popover>
  );
}
